import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

type FaviconSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZE: Record<FaviconSize, { box: string; text: string }> = {
  xs: { box: 'h-4 w-4', text: 'text-[8px]' },
  sm: { box: 'h-7 w-7', text: 'text-[10px]' },
  md: { box: 'h-9 w-9', text: 'text-[11px]' },
  lg: { box: 'h-10 w-10', text: 'text-xs' },
};

/** Hostname from a website or feed URL, or null if it cannot be parsed. */
export function hostnameFromUrl(raw?: string | null): string | null {
  if (!raw?.trim()) return null;
  const value = raw.trim();
  try {
    const url = new URL(value.includes('://') ? value : `https://${value}`);
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    return host || null;
  } catch {
    return null;
  }
}

export function outletFaviconSrc(website?: string | null, feedUrl?: string | null): string | null {
  const host = hostnameFromUrl(website) ?? hostnameFromUrl(feedUrl);
  if (!host) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`;
}

function initialsFromName(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function InitialsFallback({
  name,
  size,
  className,
}: {
  name: string;
  size: FaviconSize;
  className?: string;
}) {
  const { box, text } = SIZE[size];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-lg bg-slate-800 font-semibold text-white',
        box,
        text,
        className,
      )}
      aria-hidden
    >
      {initialsFromName(name) || '?'}
    </span>
  );
}

/**
 * Outlet mark: the site favicon when we have a website or feed URL,
 * otherwise the same initials tile used before.
 */
export function OutletFavicon({
  name,
  website,
  feedUrl,
  size = 'md',
  className,
}: {
  name: string;
  website?: string | null;
  feedUrl?: string | null;
  size?: FaviconSize;
  className?: string;
}) {
  const src = outletFaviconSrc(website, feedUrl);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return <InitialsFallback name={name} size={size} className={className} />;
  }

  const { box } = SIZE[size];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white',
        box,
        className,
      )}
      title={name}
    >
      <img
        src={src}
        alt=""
        className="h-full w-full object-contain p-1"
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    </span>
  );
}
